import { useFirm, useFirmMatters } from '@/features/firms/hooks';
import {
  Anchor,
  Badge,
  Breadcrumbs,
  Paper,
  SimpleGrid,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { LoadingView } from '@practist/components';
import { Building2Icon, ClockIcon, FilesIcon, Users2Icon } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import styles from './Firm.module.css';
import StatCard from './StatCard';
import { formatRelativeDate } from './dateUtils';
import MattersTable from './MattersTable';
import { getPractitionerTitleLabel } from './practitionerTitle';

const Firm = () => {
  const { firmId } = useParams();
  const firm = useFirm(firmId);
  const { matters, isLoading: mattersLoading } = useFirmMatters(firmId);

  if (!firm) {
    return <LoadingView includeAnimation={true} />;
  }

  return (
    <div className={styles.root}>
      <Breadcrumbs>
        <Anchor component={Link} to='/firms'>
          Firms
        </Anchor>
        <Text>{firm.firmName}</Text>
      </Breadcrumbs>
      <Title order={2} mt='xl' mb='xl'>
        {firm.firmName}
      </Title>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} mb='xl'>
        <StatCard
          stat={{
            title: 'People',
            value: firm.numHumans.toString(),
            icon: <Users2Icon />,
          }}
        />
        <StatCard
          stat={{
            title: 'Companies',
            value: firm.numOrganizations.toString(),
            icon: <Building2Icon />,
          }}
        />
        <StatCard
          stat={{
            title: 'Last Sign In',
            value: formatRelativeDate(firm.lastSignInTime),
            subtitle: firm.lastSignInTime
              ? new Date(firm.lastSignInTime).toLocaleString()
              : undefined,
            icon: <ClockIcon />,
          }}
        />
        <StatCard
          stat={{
            title: 'Last Active',
            value: formatRelativeDate(firm.lastRefreshTime),
            subtitle: firm.lastRefreshTime
              ? new Date(firm.lastRefreshTime).toLocaleString()
              : undefined,
            icon: <ClockIcon />,
          }}
        />
      </SimpleGrid>

      <Tabs defaultValue='users'>
        <Tabs.List>
          <Tabs.Tab value='users' leftSection={<Users2Icon size={16} />}>
            Users ({firm.users.length})
          </Tabs.Tab>
          <Tabs.Tab value='matters' leftSection={<FilesIcon size={16} />}>
            Matters ({firm.numMatters})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value='users' pt='md'>
          <Paper shadow='sm' p='md' withBorder>
            <Title order={3} mb='md'>
              Users
            </Title>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Roles</Table.Th>
                  <Table.Th>Attorney / LLP</Table.Th>
                  <Table.Th>Auth ID</Table.Th>
                  <Table.Th>Source</Table.Th>
                  <Table.Th>Disabled</Table.Th>
                  <Table.Th>Last Sign In</Table.Th>
                  <Table.Th>Last Active</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {firm.users.map((user) => (
                  <Table.Tr key={user.id}>
                    <Table.Td>
                      {user.firstName} {user.lastName}
                    </Table.Td>
                    <Table.Td>{user.email || '—'}</Table.Td>
                    <Table.Td>
                      {user.roles.map((role) => (
                        <Badge key={role} size='sm' mr='xs'>
                          {role}
                        </Badge>
                      ))}
                    </Table.Td>
                    <Table.Td>
                      {getPractitionerTitleLabel(user) ?? '—'}
                    </Table.Td>
                    <Table.Td>
                      <Text size='xs' c='dimmed'>
                        {user.authId || '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {user.source && (
                        <Text size='sm'>
                          {user.source}
                          {user.sourceId && (
                            <Text size='xs' c='dimmed'>
                              {user.sourceId}
                            </Text>
                          )}
                        </Text>
                      )}
                      {!user.source && '—'}
                    </Table.Td>
                    <Table.Td>{user.disable ? 'Yes' : 'No'}</Table.Td>
                    <Table.Td>
                      {user.lastSignInTime ? (
                        <>
                          <Text size='sm'>
                            {formatRelativeDate(user.lastSignInTime)}
                          </Text>
                          <Text size='xs' c='dimmed'>
                            {new Date(user.lastSignInTime).toLocaleString()}
                          </Text>
                        </>
                      ) : (
                        '—'
                      )}
                    </Table.Td>
                    <Table.Td>
                      {user.lastRefreshTime ? (
                        <>
                          <Text size='sm'>
                            {formatRelativeDate(user.lastRefreshTime)}
                          </Text>
                          <Text size='xs' c='dimmed'>
                            {new Date(user.lastRefreshTime).toLocaleString()}
                          </Text>
                        </>
                      ) : (
                        '—'
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value='matters' pt='md'>
          <MattersTable matters={matters} loading={mattersLoading} />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};

export default Firm;
